'use client';

import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import {
  type BackgroundColor,
  backgroundToTokens,
} from '@ironhack/design-system2/utils';
import { redirect } from 'next/navigation';
import { propEq } from 'ramda';
import { type ReactElement } from 'react';

import { Storytelling } from '@/app/components/client/storytelling';
import { getPageUrl, getUrlRegion } from '@/app/urls/url-mapping';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import {
  type DatoButtonBlock,
  type DatoImage,
  type DatoModuleFormat,
} from '@/lib/datocms/types';
import { type UrlRegion } from '@/types';

export type NotFoundContentData = {
  header: {
    buttonText: string;
    description: string;
    image: DatoImage;
    title: string;
  };
  headerBackground: BackgroundColor;
  urlRegion: UrlRegion;
};

type Props = {
  data: NotFoundContentData[];
} & FlexProps;

export const NotFoundContent = (props: Props): ReactElement => {
  const { data, ...flexProps } = props;

  const { params } = usePageContext();
  const urlRegion = getUrlRegion(params);
  const path = getPageUrl('home', params) ?? '/';

  const contentData = data.find(propEq('urlRegion', urlRegion));
  if (!contentData) redirect(path);

  const {
    header: { buttonText, description, image, title },
    headerBackground,
  } = contentData;

  const button: [DatoButtonBlock] = [
    {
      target: [{ route: { path } }],
      text: buttonText,
      variant: 'primary',
    },
  ];
  const moduleFormat: [DatoModuleFormat] = [
    { background: 'transparent', spacingBottom: '80', spacingTop: '40' },
  ];
  const storytellingData: Storytelling = {
    columns: [
      { image },
      {
        button,
        flexProps: { my: 'auto' },
        subtitle: description,
        title,
      },
    ],
    headingProps: { as: 'h1' },
    moduleFormat,
  };

  const { bg } = backgroundToTokens(headerBackground);

  return (
    <Flex bg={bg} direction="column" h="100vh" {...flexProps}>
      <Storytelling data={storytellingData} my="auto" />
    </Flex>
  );
};
